import type { NullableRangeDto, RangeDto } from '@swipe4work/models-orval'
import dayjs from 'dayjs'
import { isNumber } from '@primevue/core'

export function toPercentage(fraction: number) {
  return `${Math.round(fraction * 100)}%`
}

export function range(range: RangeDto | undefined) {
  if (range === undefined) {
    return ''
  }

  return `${range.lower} - ${range.upper}`
}

export function nullableRange(range: NullableRangeDto | null | undefined) {
  if (range == null) {
    return ''
  }

  if (!range.upper) {
    return `${range.lower}+`
  }

  return `${range.lower} - ${range.upper}`
}

export function showPhoneNumber(phoneNumber: string) {
  // pattern for +31 X XXX XXX XX
  const pattern = /^(\+31)(6)(\d{3})(\d{3})(\d{2})$/

  // Check if the phone number matches the pattern
  if (pattern.test(phoneNumber)) {
    // Use the pattern to match and replace the phone number
    return phoneNumber.replace(pattern, '06 $3 $4 $5')
  }

  // Return the original phone number if it doesn't match the format
  return phoneNumber

}

/**
 * Display the time when the date is today, else display the date and time.
 */
export function timeOrDateTime(date: Date) {
  const t = useI18n()
  const parsedDate = dayjs(date)
  if (parsedDate.isSame(dayjs(), 'day')) {
    return parsedDate.format('HH:mm')
  }

  if (parsedDate.isSame(dayjs().add(-1, 'd'), 'day')) {
    return `${t.t('yesterday')}, ${parsedDate.format('HH:mm')}`
  }

  return parsedDate.format('DD-MM, HH:mm')
}

export const initials = (name: string) => {
  const parts = name.trim().split(' ')
  const first = parts[0]?.[0] || ''
  const last = parts[parts.length - 1]?.[0] || ''

  return (first + last).toUpperCase()
}

/**
 * Display the time when the date is today or yesterday, else display the date.
 */
export function timeOrDate(date: Date | null | undefined, addPreposition = false) {
  if (!date) {
    return ''
  }

  const t = useI18n()
  const parsedDate = dayjs(date)
  const now = dayjs()
  if (parsedDate.isSame(now, 'day')) {
    if (addPreposition) {
      return `${t.t('today')} ${t.t('at')} ${parsedDate.format('HH:mm')}`
    }

    return parsedDate.format('HH:mm')
  }

  const yesterday = now.add(-1, 'd')
  if (parsedDate.isSame(yesterday, 'day')) {
    if (addPreposition) {
      return `${t.t('yesterday')} ${t.t('at')} ${parsedDate.format('HH:mm')}`
    }

    return `${t.t('yesterday')}, ${parsedDate.format('HH:mm')}`
  }

  if (addPreposition) {
    return `${t.t('on')} ${parsedDate.format('DD-MM')}`
  }

  return parsedDate.format('DD-MM-YYYY')
}

export function date(date: Date) {
  return dayjs(date).format('DD-MM-YYYY')
}

export function dateTime(date: Date | null | undefined) {
  if (!date) {
    return ''
  }

  return dayjs(date).format('DD-MM-YYYY HH:mm')
}

export function yearMonthRange(start: Date | undefined | null | number, end: Date | undefined | null | number) {
  if (!start) {
    return ''
  }

  if (isNumber(start) || isNumber(end)) {
    return yearRange(start, end)
  }

  const t = useI18n()
  if (!end) {
    return `${dayjs(start).format('MM-YYYY')} - ${t.t('present')}`
  }

  return `${dayjs(start).format('MM-YYYY')} - ${dayjs(end).format('MM-YYYY')}`
}

export function yearRange(start: number | null | undefined | Date, end: number | undefined | null | Date) {
  const t = useI18n()
  if (start instanceof  Date ) {
    start = start.getFullYear()
  }

  if (end instanceof Date) {
    end = end.getFullYear()
  }

  if (start === null || start === undefined) {
    return ''
  }

  if (!end) {
    return `${start} - ${t.t('present')}`
  }

  if (start === end) {
    return `${start}`
  }

  return `${start} - ${end}`
}

/**
 * Display the amount of years and months between a start and end
 * @returns
 */
export function yearMonthDuration(start: Date, end: Date | undefined | null) {
  const t = useI18n()
  const from = dayjs(start)
  let till = dayjs(end)
  if (!till) {
    till = dayjs()
  } // set till to now

  const yearDiff = till.diff(from, 'year')
  const monthDiff = till.diff(from, 'month') % 12
  if (yearDiff >= 5 || monthDiff === 0) {
    return `${yearDiff} ${t.t('year').toLocaleLowerCase()}`
  }

  if (yearDiff === 0) {
    return `${monthDiff} ${t.t('month', monthDiff).toLocaleLowerCase()}`
  }

  return `${yearDiff} ${t.t('year').toLocaleLowerCase()}, ${monthDiff} ${t.t('month', monthDiff).toLocaleLowerCase()}`
}
